import React, { useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from '@/components/PageHeader';
import { Star } from 'lucide-react';

interface Section {
  title: string;
  questions: Question[];
  openQuestion?: OpenQuestion;
}

interface Question {
  id: keyof typeof feedbackFields;
  text: string;
}

interface OpenQuestion {
  id: keyof typeof feedbackFields;
  text: string;
}

const sections: Section[] = [
  {
    title: "Experiencia General",
    questions: [
      { id: "expectations_met", text: "La plataforma cumplió con mis expectativas en términos de funcionalidad y utilidad." },
      { id: "information_clarity", text: "Encontré la información que necesitaba de manera clara y sencilla." },
      { id: "intuitive_navigation", text: "La experiencia de navegación en MigranteApp fue intuitiva." },
      { id: "positive_experience", text: "Mi experiencia general con MigranteApp ha sido positiva." }
    ],
    openQuestion: {
      id: "expectations_details",
      text: "¿Puede describir algún aspecto específico de la plataforma que haya superado o no haya cumplido sus expectativas?"
    }
  },
  {
    title: "Atención y Soporte",
    questions: [
      { id: "support_usefulness", text: "La asistencia recibida a través de MigranteApp fue útil y oportuna." },
      { id: "resource_effectiveness", text: "Pude resolver mis dudas o problemas a través de los recursos disponibles en la plataforma." },
      { id: "response_times", text: "Los tiempos de respuesta del equipo de soporte fueron adecuados." }
    ],
    openQuestion: {
      id: "support_feedback",
      text: "¿Hubo algún aspecto del soporte o la atención al usuario que le haya parecido excepcional o que necesite mejorar?"
    }
  },
  {
    title: "Usabilidad y Accesibilidad",
    questions: [
      { id: "ease_of_use", text: "La plataforma es fácil de usar y entender." },
      { id: "accessibility", text: "La información y los servicios ofrecidos son accesibles para todos los usuarios." },
      { id: "clear_processes", text: "Los procesos de registro y uso de MigranteApp fueron claros y sencillos." }
    ],
    openQuestion: {
      id: "usability_suggestions",
      text: "¿Qué cambios sugeriría para mejorar la experiencia de uso de MigranteApp?"
    }
  },
  {
    title: "Diseño de la Plataforma",
    questions: [
      { id: "visual_appeal", text: "El diseño visual de MigranteApp es atractivo y moderno." },
      { id: "readability", text: "Los colores y tipografías facilitan la lectura y comprensión de la información." },
      { id: "intuitive_layout", text: "La disposición de los elementos en la plataforma es intuitiva y ordenada." }
    ],
    openQuestion: {
      id: "design_suggestions",
      text: "¿Qué aspectos del diseño de MigranteApp mejorarías para hacerla más atractiva o funcional?"
    }
  },
  {
    title: "Recomendación y Fidelidad",
    questions: [
      { id: "would_recommend", text: "Recomendaría MigranteApp a otros migrantes." },
      { id: "future_use", text: "Es probable que vuelva a usar la plataforma en el futuro." },
      { id: "needs_consideration", text: "Siento que MigranteApp se preocupa por las necesidades de los migrantes." }
    ],
    openQuestion: {
      id: "recommendation_factors",
      text: "¿Qué factor influiría en su decisión de recomendar o no nuestra plataforma?"
    }
  }
];

const feedbackFields = {
  expectations_met: "",
  information_clarity: "",
  intuitive_navigation: "",
  positive_experience: "",
  expectations_details: "",
  support_usefulness: "",
  resource_effectiveness: "",
  response_times: "",
  support_feedback: "",
  ease_of_use: "",
  accessibility: "",
  clear_processes: "",
  usability_suggestions: "",
  visual_appeal: "",
  readability: "",
  intuitive_layout: "",
  design_suggestions: "",
  would_recommend: "",
  future_use: "",
  needs_consideration: "",
  recommendation_factors: "",
  additional_comments: ""
} as const;

const Feedback = () => {
  const session = useSession();
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [textResponses, setTextResponses] = useState<Record<string, string>>({});
  const [hoveredRatings, setHoveredRatings] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRatingHover = (questionId: string, value: number) => {
    setHoveredRatings(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleRatingClick = (questionId: string, value: number) => {
    setRatings(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleTextChange = (questionId: string, value: string) => {
    setTextResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const isFormValid = () => {
    const hasAnyRating = Object.values(ratings).length > 0;
    const hasAnyTextResponse = Object.values(textResponses).some(response => response.trim() !== '');
    
    return hasAnyRating || hasAnyTextResponse;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user?.id) {
      toast.error("Debes iniciar sesión para enviar comentarios");
      return;
    }

    if (!isFormValid()) {
      toast.error("Por favor proporciona al menos una calificación o un comentario");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const feedbackData = {
        user_id: session.user.id,
        ...ratings,
        ...textResponses,
        additional_comments: textResponses.additional_comments || null
      };

      const { error } = await supabase.from('feedback').insert(feedbackData);

      if (error) throw error;

      toast.success("¡Gracias por tus comentarios!");
      setRatings({});
      setTextResponses({});
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error("Error al enviar los comentarios");
    } finally {
      setIsSubmitting(false);
    }
  };

  const RatingStars = ({ questionId }: { questionId: string }) => (
    <div className="flex justify-center gap-2">
      {[1, 2, 3, 4, 5].map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => handleRatingClick(questionId, value)}
          onMouseEnter={() => handleRatingHover(questionId, value)}
          onMouseLeave={() => handleRatingHover(questionId, 0)}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <Star
            className={`h-8 w-8 ${
              value <= (hoveredRatings[questionId] || ratings[questionId] || 0)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <PageHeader 
        title="Encuesta de Retroalimentación de MigranteApp"
        description="Por favor, responda las siguientes preguntas según su experiencia con nuestra plataforma. No es necesario responder todas las preguntas, pero agradecemos cualquier retroalimentación que pueda proporcionarnos."
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {sections.map((section) => (
              <div key={section.title} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  {section.title}
                </h3>
                
                {section.questions.map((question) => (
                  <div key={question.id} className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {question.text}
                      </label>
                    </div>
                    <RatingStars questionId={question.id} />
                  </div>
                ))}

                {section.openQuestion && (
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700">
                      {section.openQuestion.text}
                    </label>
                    <Textarea
                      value={textResponses[section.openQuestion.id] || ''}
                      onChange={(e) => handleTextChange(section.openQuestion!.id, e.target.value)}
                      placeholder="Comparte tu opinión..."
                      className="min-h-[120px]"
                    />
                  </div>
                )}
              </div>
            ))}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Comentario final
              </label>
              <Textarea
                value={textResponses.additional_comments || ''}
                onChange={(e) => handleTextChange('additional_comments', e.target.value)}
                placeholder="Si tiene algún comentario adicional que desee compartir, por favor escríbalo aquí..."
                className="min-h-[120px]"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? "Enviando..." : "Enviar comentarios"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
